/**
 * Formulario da campanha de e-mail e suas regras puras.
 *
 * O compositor e o mesmo para criar e editar: `formularioDeCampanha` traduz
 * uma campanha existente para o formulario, e `montarCampanha` faz o caminho
 * de volta. Agendamento monta o instante como hora LOCAL antes de virar ISO
 * (ver ModalAgendar para o porque).
 */

import type { EmailCampaign, EmailCampaignStatus, SegmentKey } from './types';

export interface FormularioCampanhaEmail {
  readonly companyId: string;
  readonly subject: string;
  readonly preheader: string;
  readonly body: string;
  readonly ctaLabel: string;
  readonly ctaUrl: string;
  readonly segmentKey: SegmentKey;
  readonly agendar: boolean;
  /** YYYY-MM-DD */
  readonly dataAgendamento: string;
  /** HH:MM */
  readonly horaAgendamento: string;
}

export const VARIAVEIS_DO_EMAIL: readonly string[] = ['{primeiro_nome}', '{produto}', '{link}'];

export function formularioEmailInicial(companyId: string, amanha: string): FormularioCampanhaEmail {
  return {
    companyId,
    subject: '',
    preheader: '',
    body: '',
    ctaLabel: '',
    ctaUrl: '',
    segmentKey: 'abandonaram_sem_pagar',
    agendar: false,
    dataAgendamento: amanha,
    horaAgendamento: '09:00',
  };
}

export function formularioDeCampanha(campanha: EmailCampaign, amanha: string): FormularioCampanhaEmail {
  const agendada = campanha.scheduledFor !== null;
  const instante = agendada && campanha.scheduledFor !== null ? new Date(campanha.scheduledFor) : null;
  return {
    companyId: campanha.companyId,
    subject: campanha.subject,
    preheader: campanha.preheader,
    body: campanha.body,
    ctaLabel: campanha.ctaLabel,
    ctaUrl: campanha.ctaUrl,
    segmentKey: campanha.segmentKey,
    agendar: agendada,
    dataAgendamento: instante === null ? amanha : dataLocal(instante),
    horaAgendamento: instante === null ? '09:00' : horaLocal(instante),
  };
}

function dataLocal(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function horaLocal(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Instante do agendamento em ISO, ou `null` quando invalido ou nao agendado. */
export function instanteAgendado(form: FormularioCampanhaEmail): string | null {
  if (!form.agendar) {
    return null;
  }
  const instante = new Date(`${form.dataAgendamento}T${form.horaAgendamento}:00`);
  return Number.isNaN(instante.getTime()) ? null : instante.toISOString();
}

export function pendenciasDaCampanha(form: FormularioCampanhaEmail): readonly string[] {
  const pendencias: string[] = [];
  if (form.subject.trim() === '') {
    pendencias.push('escreva o assunto');
  }
  if (form.body.trim() === '') {
    pendencias.push('escreva o corpo do e-mail');
  }
  if (form.ctaLabel.trim() !== '' && !/^https?:\/\/\S+$/i.test(form.ctaUrl.trim())) {
    pendencias.push('informe a URL do botão (https://...)');
  }
  if (form.agendar) {
    const iso = instanteAgendado(form);
    if (iso === null) {
      pendencias.push('data ou hora do agendamento inválida');
    } else if (new Date(iso).getTime() <= Date.now()) {
      pendencias.push('escolha um horário de agendamento no futuro');
    }
  }
  return pendencias;
}

export function montarCampanha(form: FormularioCampanhaEmail, status: EmailCampaignStatus, id: string, totalRecipients: number, existente: EmailCampaign | null): EmailCampaign {
  return {
    id,
    companyId: form.companyId,
    subject: form.subject.trim(),
    preheader: form.preheader.trim(),
    body: form.body.trim(),
    ctaLabel: form.ctaLabel.trim(),
    ctaUrl: form.ctaUrl.trim(),
    segmentKey: form.segmentKey,
    status,
    createdAt: existente === null ? new Date().toISOString() : existente.createdAt,
    scheduledFor: status === 'agendada' ? instanteAgendado(form) : null,
    totalRecipients: status === 'rascunho' ? 0 : totalRecipients,
    acceptedCount: 0,
    failedCount: 0,
    openedCount: 0,
    clickedCount: 0,
    unsubscribedCount: 0,
    paidCount: 0,
    recoveredAmountCents: 0,
    commissionCents: 0,
    daily: [],
  };
}
